// Helper function to update row data with WebSocket messages
export const updateRowData = (prevRow, side, msg) => {
  if (!msg || !prevRow) return prevRow;

  const currLTP = msg.lastTradedPrice || msg.ltp || 0;
  const oi = msg.currentOpenInterest !== undefined ? msg.currentOpenInterest : (prevRow[`${side}_oi`] || 0);
  const volume = msg.volume !== undefined ? msg.volume : (prevRow[`${side}_volume`] || 0);
  const bidPrice = msg.bestBidPrice || msg.bidPrice || 0;
  const askPrice = msg.bestAskPrice || msg.askPrice || 0;
  const closePrice = msg.closePrice !== undefined ? msg.closePrice : (prevRow[`${side}_close`] || 0);
  
  const initialOi = (msg.initialOpenInterest !== undefined && msg.initialOpenInterest > 0)
    ? msg.initialOpenInterest 
    : (prevRow[`${side}_initial_oi`] || oi);
    
  let oiChangePercent = 0;
  if (initialOi > 0) {
    oiChangePercent = ((oi - initialOi) / initialOi) * 100;
  }

  // Calculate change
  const changePct = msg.percentChange !== undefined 
    ? msg.percentChange 
    : (closePrice ? ((currLTP - closePrice) / closePrice) * 100 : 0);
  
  const changeAbs = msg.absoluteChange !== undefined
    ? msg.absoluteChange
    : (currLTP - closePrice);

  const delta = msg.delta !== undefined ? msg.delta : prevRow[`${side}_delta`] || 0;
  const gamma = msg.gamma !== undefined ? msg.gamma : prevRow[`${side}_gamma`] || 0;
  const theta = msg.theta !== undefined ? msg.theta : prevRow[`${side}_theta`] || 0;
  const vega = msg.vega !== undefined ? msg.vega : prevRow[`${side}_vega`] || 0;
  const iv = msg.iv !== undefined ? msg.iv : prevRow[`${side}_iv`] || 0;
  const rho = msg.rho !== undefined ? msg.rho : prevRow[`${side}_rho`] || 0;

  return {
    ...prevRow,
    [`${side}_data`]: {
      ...prevRow[`${side}_data`],
      ltp: currLTP,
      bestBidPrice: bidPrice,
      bestAskPrice: askPrice,
      // Preserve the token and other important fields
      token: prevRow[`${side}_data`]?.token || prevRow[`${side}_token`] || '',
      trading_symbol: prevRow[`${side}_data`]?.trading_symbol || '',
      display_name: prevRow[`${side}_data`]?.display_name || '',
      exchange: prevRow[`${side}_data`]?.exchange || ''
    },
    [`${side}_ltp`]: currLTP,
    [`${side}_oi`]: oi,
    [`${side}_volume`]: volume,
    [`${side}_close`]: closePrice,
    [`${side}_change`]: changeAbs,
    [`${side}_change%`]: changePct,
    [`${side}_bid`]: bidPrice,
    [`${side}_ask`]: askPrice,
    [`${side}_delta`]: delta,
    [`${side}_gamma`]: gamma,
    [`${side}_theta`]: theta,
    [`${side}_vega`]: vega,
    [`${side}_iv`]: iv,
    [`${side}_rho`]: rho,
    [`${side}_initial_oi`]: initialOi,
    [`${side}_oi_change_percent`]: oiChangePercent
  };
};

// Function to find the nearest strike price to the current spot price
export const findNearestStrikeIndex = (rows, spotPrice) => {
  if (!rows.length || !spotPrice) return -1;

  let nearestIndex = 0;
  let minDiff = Math.abs(rows[0].strike_price - spotPrice);

  rows.forEach((row, index) => {
    const diff = Math.abs(row.strike_price - spotPrice);
    if (diff < minDiff) {
      minDiff = diff;
      nearestIndex = index;
    }
  });

  return nearestIndex;
};

// Function to determine if a strike is ITM, ATM, or OTM
export const getOptionStatus = (strikePrice, spotPrice, isCall) => {
  if (!spotPrice) return 'otm';

  const diff = Math.abs(strikePrice - spotPrice);
  const percentDiff = (diff / spotPrice) * 100;

  // If within 0.5% of spot price, consider it ATM
  if (percentDiff < 0.5) return 'atm';

  // For calls: ITM if strike < spot, OTM if strike > spot
  // For puts: ITM if strike > spot, OTM if strike < spot
  if (isCall) {
    return strikePrice < spotPrice ? 'itm' : 'otm';
  } else {
    return strikePrice > spotPrice ? 'itm' : 'otm';
  }
};

// Calculate the difference percentage for ATM strike
export const getStrikeDiffPercentage = (strikePrice, spotPrice) => {
  if (!spotPrice || !strikePrice) return 0;
  return ((strikePrice - spotPrice) / spotPrice * 100).toFixed(2);
};

// Function to determine if an option has no activity (dead)
export const isOptionDead = (row, side) => {
  const oi = parseFloat(row[`${side}_oi`]) || 0;
  const volume = parseFloat(row[`${side}_volume`]) || 0;
  // const ltp = parseFloat(row[`${side}_ltp`]) || 0;

  // Dead if OI is 0 AND Volume is 0 AND LTP is 0 (or null/undefined)
  return oi === 0 && volume === 0;
};

// Initialize dynamic rows from raw data
export const initializeDynamicRows = (arrRows, exchange) => {
  return arrRows.map(row => {
    const callOption = row.call_option || {};
    const putOption = row.put_option || {};

    const ceExchange = (callOption.exchange === "BSE" || exchange === "BSE") ? "BFO" : 
                       (callOption.exchange === "NSE" || exchange === "NSE") ? "NFO" : 
                       (callOption.exchange || exchange);

    const peExchange = (putOption.exchange === "BSE" || exchange === "BSE") ? "BFO" : 
                       (putOption.exchange === "NSE" || exchange === "NSE") ? "NFO" : 
                       (putOption.exchange || exchange);

    // Resolve LTP with multiple fallbacks (especially for Commodities which only send close_price initially)
    const ceLtpResolved = parseFloat(callOption.ltp || callOption.last_traded_price || callOption.price || callOption.close_price || 0);
    const peLtpResolved = parseFloat(putOption.ltp || putOption.last_traded_price || putOption.price || putOption.close_price || 0);

    const ceCloseResolved = parseFloat(callOption.close_price) || 0;
    const peCloseResolved = parseFloat(putOption.close_price) || 0;

    // Calculate initial changes
    const ceAbsChange = callOption.net_change ?? callOption.absolute_change ?? (ceLtpResolved - ceCloseResolved);
    const peAbsChange = putOption.net_change ?? putOption.absolute_change ?? (peLtpResolved - peCloseResolved);

    const ceChange = callOption.percent_change ?? callOption.change_percent ?? (ceCloseResolved !== 0 ? (ceAbsChange / ceCloseResolved * 100) : 0);
    const peChange = putOption.percent_change ?? putOption.change_percent ?? (peCloseResolved !== 0 ? (peAbsChange / peCloseResolved * 100) : 0);

    const ceToken = callOption.token || '';
    const peToken = putOption.token || '';

    const ceCurrentOi = callOption.currentOpenInterest || callOption.open_interest || callOption.openInterest || 0;
    const peCurrentOi = putOption.currentOpenInterest || putOption.open_interest || putOption.openInterest || 0;
    
    const ceInitialOi = callOption.initialOpenInterest || callOption.initial_open_interest || ceCurrentOi;
    const peInitialOi = putOption.initialOpenInterest || putOption.initial_open_interest || peCurrentOi;

    const ceOiChangePct = ceInitialOi > 0 ? ((ceCurrentOi - ceInitialOi) / ceInitialOi) * 100 : 0;
    const peOiChangePct = peInitialOi > 0 ? ((peCurrentOi - peInitialOi) / peInitialOi) * 100 : 0;

    return {
      ...row,
      call_option: row.call_option ? { ...row.call_option, exchange: ceExchange } : undefined,
      put_option: row.put_option ? { ...row.put_option, exchange: peExchange } : undefined,
      ce_data: {
        trading_symbol: callOption.trading_symbol || '',
        display_name: callOption.display_name || '',
        ltp: ceLtpResolved,
        close: ceCloseResolved,
        exchange: ceExchange,
        token: ceToken,
        lotsize: callOption.lotsize || 0,
        tick_size: callOption.tick_size || 0,
        execution: callOption.execution || '',
        bestBidPrice: callOption.bestBidPrice || 0,
        bestAskPrice: callOption.bestAskPrice || 0,
        tradable: callOption.tradable
      },
      pe_data: {
        trading_symbol: putOption.trading_symbol || '',
        display_name: putOption.display_name || '',
        ltp: peLtpResolved,
        close: peCloseResolved,
        exchange: peExchange,
        token: peToken,
        lotsize: putOption.lotsize || 0,
        tick_size: putOption.tick_size || 0,
        execution: putOption.execution || '',
        bestBidPrice: putOption.bestBidPrice || 0,
        bestAskPrice: putOption.bestAskPrice || 0,
        tradable: putOption.tradable
      },
      ce_token: ceToken,
      pe_token: peToken,
      ce_ltp: ceLtpResolved,
      pe_ltp: peLtpResolved,
      ce_oi: ceCurrentOi,
      pe_oi: peCurrentOi,
      ce_initial_oi: ceInitialOi,
      pe_initial_oi: peInitialOi,
      ce_oi_change_percent: ceOiChangePct,
      pe_oi_change_percent: peOiChangePct,
      ce_close: ceCloseResolved,
      pe_close: peCloseResolved,
      ce_volume: callOption.volume || 0,
      pe_volume: putOption.volume || 0,
      ce_change: ceChange || 0,
      pe_change: peChange || 0,
      ce_abs_change: ceAbsChange || 0,
      pe_abs_change: peAbsChange || 0,
      ce_bid: callOption.bestBidPrice || 0,
      pe_bid: putOption.bestBidPrice || 0,
      ce_ask: callOption.bestAskPrice || 0,
      pe_ask: putOption.bestAskPrice || 0,
      ce_delta: callOption.delta || 0,
      pe_delta: putOption.delta || 0,
      ce_gamma: callOption.gamma || 0,
      pe_gamma: putOption.gamma || 0,
      ce_theta: callOption.theta || 0,
      pe_theta: putOption.theta || 0,
      ce_vega: callOption.vega || 0,
      pe_vega: putOption.vega || 0,
      ce_iv: callOption.iv || 0,
      pe_iv: putOption.iv || 0,
      ce_rho: callOption.rho || 0,
      pe_rho: putOption.rho || 0,
      strike_price: row.strike_price || 0,
      ce_exchange: ceExchange,
      pe_exchange: peExchange
    };
  });
}; 